// Single authoritative time base for all match clocks (game, penalty, inferiority,
// timeout, break). Per the 002 plan (research D10 + contracts/core-engine.md):
//
//   - A clock is described by `{ durationMs, startedAt }`: a frozen remaining duration
//     plus the timestamp at which it (re)started running (null while paused).
//   - Remaining time is ALWAYS computed from the timestamp — never tracked by an
//     independent decrementing counter. Therefore clocks cannot drift apart from each
//     other or across windows; they are pure functions of a single shared `now`.
//   - One tick generator (~100 ms) drives recomputation/sync; higher-precision display
//     (e.g. hundredths in the final 10 s) is a render-only concern (see formatClock).

/** Create a paused clock with the given remaining duration (ms). */
export function createClock(durationMs) {
  return { durationMs: Math.max(0, durationMs), startedAt: null };
}

/** Start (or resume) a clock at `now`. No-op if already running. Preserves extra fields. */
export function startClock(clock, now) {
  if (clock.startedAt !== null) return clock;
  return { ...clock, startedAt: now };
}

/**
 * Pause a clock at `now`, freezing it at the second it is DISPLAYING. No-op if already paused.
 * Preserves extra fields.
 *
 * Why the displayed second and not the raw remaining: the board is the official time. Stopping at
 * 12:34.6 and storing 12:34.6 would leave the clock a fraction out of step with every whole-second
 * clock around it — a penalty entered afterwards runs on whole seconds, so on the next start the two
 * would flip their displayed second a few hundred ms apart. Freezing at 12:35 (what the hall reads)
 * keeps every clock on whole seconds, which is what makes them switch in lockstep with no tolerance.
 * The displayed value does not change at the moment of stopping.
 */
export function stopClock(clock, now) {
  if (clock.startedAt === null) return clock;
  const displayed = Math.ceil(remainingMs(clock, now) / 1000) * 1000;
  return { ...clock, durationMs: displayed, startedAt: null };
}

/**
 * Remaining time (ms) of a clock at `now`, computed from the timestamp.
 *
 * Clamped at BOTH ends: never negative, and never more than the clock's own duration — a countdown
 * must not count up. The upper clamp matters because clocks are read at the shared display beat
 * (see displayBeat), which can fall a fraction of a second BEFORE a clock that has just been
 * started; without it a fresh 2:00 penalty would read 2:00 + a few hundred ms and show as "02:01"
 * for its first moment.
 */
export function remainingMs(clock, now) {
  if (clock.startedAt === null) return Math.max(0, clock.durationMs);
  const elapsed = Math.max(0, now - clock.startedAt);
  return Math.max(0, clock.durationMs - elapsed);
}

export function isRunning(clock) {
  return clock.startedAt !== null;
}

/**
 * Render-only formatter. `mm:ss` by default; with `{ hundredths: true }` it appends
 * `.cc` — used to show the game clock's final seconds at higher precision WITHOUT
 * changing the authoritative tick or the clock state.
 */
export function formatClock(ms, { hundredths = false } = {}) {
  const safe = Math.max(0, ms);
  const totalSeconds = Math.floor(safe / 1000);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  const base = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  if (!hundredths) return base;
  const cs = Math.floor((safe % 1000) / 10);
  return `${base}.${String(cs).padStart(2, '0')}`;
}

/**
 * The DISPLAY BEAT: the most recent instant ≤ `now` at which the running game clock's remaining
 * time was an exact whole second. Deriving EVERY countdown at this instant (instead of at the raw
 * `now`) makes all displayed timers — game clock, penalties, inferiority, timeout, break — flip
 * their second in lockstep, by construction.
 *
 * Why this is needed: a displayed second flips when `remaining` crosses a whole second, i.e. at
 * phase `(startedAt + durationMs) mod 1000`. Two clocks share that phase only if BOTH their start
 * instants AND their durations are aligned. Stopping the game clock mid-second leaves it a
 * fractional remaining (e.g. 592'700 ms), so any penalty added afterwards — a whole 120'000 ms —
 * runs a few hundred ms out of phase with it and visibly flips at a different moment.
 *
 * Quantizing the read instant sidesteps the whole problem: no stored time is modified, so no
 * playing time is gained or lost, and the game clock's own display is unaffected (at the beat its
 * remaining is exactly the value `ceil` would have produced at `now`) — the horn and the period
 * change still land on the true zero crossing.
 *
 * While the clock is paused nothing is counting down, so `now` is returned unchanged.
 */
export function displayBeat(clock, now) {
  if (!clock || clock.startedAt === null || clock.startedAt === undefined) return now;
  const r = remainingMs(clock, now);
  // Distance back to the instant where `r` was a whole second (0 when it already is).
  return now - ((1000 - (r % 1000)) % 1000);
}

/**
 * The single tick generator. One interval drives all derived recomputation and sync,
 * so every clock in the UI updates from the same beat (no divergence). Default 100 ms.
 */
export function createTicker(intervalMs = 100) {
  let handle = null;
  const listeners = new Set();
  return {
    intervalMs,
    start() {
      if (handle !== null) return;
      handle = setInterval(() => {
        for (const l of [...listeners]) l();
      }, intervalMs);
    },
    stop() {
      if (handle !== null) {
        clearInterval(handle);
        handle = null;
      }
    },
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    get running() {
      return handle !== null;
    },
  };
}
